import { motion, PanInfo } from 'framer-motion'
import { ReactNode, useState } from 'react'
import { useFeatureNavigation } from '../hooks/useFeatureNavigation'

interface FeatureSwipeWrapperProps {
  children: ReactNode
}

const FeatureSwipeWrapper = ({ children }: FeatureSwipeWrapperProps) => {
  const { navigateNext, navigatePrev, hasNext, hasPrev } = useFeatureNavigation()
  const [swipeOffset, setSwipeOffset] = useState(0)

  const SWIPE_THRESHOLD = 50 // Minimum pixels to trigger navigation
  const VELOCITY_THRESHOLD = 300 // Minimum velocity for quick swipes

  const handlePanStart = () => {
    setSwipeOffset(0)
  }

  const handlePan = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    // Only allow horizontal panning, set visual feedback
    if (Math.abs(info.offset.x) > Math.abs(info.offset.y)) {
      setSwipeOffset(info.offset.x)
    }
  }

  const handlePanEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const { offset, velocity } = info
    setSwipeOffset(0) // Reset visual offset

    // Determine if swipe was strong enough
    const isStrongSwipe = Math.abs(velocity.x) > VELOCITY_THRESHOLD
    const isLongSwipe = Math.abs(offset.x) > SWIPE_THRESHOLD

    if (!isStrongSwipe && !isLongSwipe) {
      return // Swipe not strong enough
    }

    // Swipe left (next page)
    if (offset.x < 0 && hasNext) {
      navigateNext()
    }
    // Swipe right (previous page)
    else if (offset.x > 0 && hasPrev) {
      navigatePrev()
    }
  }

  return (
    <motion.div
      onPanStart={handlePanStart}
      onPan={handlePan}
      onPanEnd={handlePanEnd}
      animate={{ x: Math.max(-100, Math.min(100, swipeOffset * 0.1)) }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      style={{
        touchAction: 'pan-y', // Allow vertical scrolling, capture horizontal swipes
      }}
      className="w-full"
    >
      {children}
    </motion.div>
  )
}

export default FeatureSwipeWrapper
